import { ChangeDetectionStrategy, Component, Input, computed, signal, inject, effect, OnDestroy, ElementRef, ViewChild, AfterViewInit, NgZone, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { LayoutService } from '@/layout/service/layout.service';
import { CloudApiService, DeviceLocationSummary } from '@/cloud/services/cloud-api.service';
import { GoogleMapsLoaderService } from '@/shared/google-maps-loader.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, filter } from 'rxjs';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

declare const google: any;

type StatusFilter = 'all' | 'active' | 'attention';

@Component({
    selector: 'app-device-map',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, FontAwesomeModule, TooltipModule, ProgressSpinnerModule, InputTextModule, EmptyStateComponent],
    templateUrl: './device-map.component.html',
    styleUrl: './device-map.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceMapComponent implements AfterViewInit, OnDestroy {
    private layoutService = inject(LayoutService);
    private api = inject(CloudApiService);
    private router = inject(Router);
    private ngZone = inject(NgZone);
    private mapsLoader = inject(GoogleMapsLoaderService);

    @ViewChild('mapContainer') mapContainer!: ElementRef<HTMLDivElement>;

    /** Namespace ID to filter locations */
    @Input() set namespaceId(value: string | null) {
        this._namespaceId.set(value);
    }

    /** Location ID to filter locations (optional) */
    @Input() set locationId(value: string | null) {
        this._locationId.set(value);
    }

    /** Max markers to show on map */
    @Input() maxMarkers = 100;

    private _namespaceId = signal<string | null>(null);
    private _locationId = signal<string | null>(null);

    /** All device locations from API */
    allLocations = signal<DeviceLocationSummary[]>([]);
    loading = signal(false);
    error = signal<string | null>(null);
    mapReady = signal(false);

    /** Expanded (full-screen) view with a searchable system list beside the map. */
    expanded = signal(false);
    search = signal('');
    statusFilter = signal<StatusFilter>('all');
    /** System highlighted in the list and on the map (expanded view). */
    selectedId = signal<string | null>(null);

    // Google Maps instances
    private map: any = null;
    private markers: any[] = [];
    private markerByDevice = new Map<string, { marker: any; devices: DeviceLocationSummary[] }>();
    private infoWindow: any = null;
    private fitTimer: ReturnType<typeof setTimeout> | null = null;

    // Moving, deleting or re-pinning a location changes where the devices below it inherit their position from.
    private locationGeoChanges = this.api.locationGeoChanged$
        .pipe(
            filter((nsId) => nsId === this._namespaceId()),
            debounceTime(500),
            takeUntilDestroyed()
        )
        .subscribe((nsId) => this.loadLocations(nsId, this._locationId() ?? undefined));

    private loadEffect = effect(() => {
        const nsId = this._namespaceId();
        const locId = this._locationId();
        if (nsId) {
            this.loadLocations(nsId, locId ?? undefined);
        } else {
            this.allLocations.set([]);
            this.clearMarkers();
        }
    });

    // Update markers when locations or map ready state changes
    private markerEffect = effect(() => {
        const locations = this.displayedLocations();
        const ready = this.mapReady();
        if (ready && this.map) {
            // Use setTimeout to ensure we're outside Angular's change detection
            setTimeout(() => this.updateMarkers(locations), 0);
        }
    });

    // Update map style when theme changes
    private themeEffect = effect(() => {
        const isDark = this.layoutService.isDarkTheme();
        const ready = this.mapReady();
        if (ready && this.map) {
            this.map.setOptions({
                styles: isDark ? this.darkMapStyles : this.lightMapStyles,
                colorScheme: isDark ? 'DARK' : 'LIGHT'
            });
        }
    });

    // The compact widget sits in a scrolling page, so wheel-zoom needs a modifier there;
    // the expanded view owns the whole viewport and can zoom freely.
    private gestureEffect = effect(() => {
        const expanded = this.expanded();
        if (this.mapReady() && this.map) {
            this.map.setOptions({ gestureHandling: expanded ? 'greedy' : 'cooperative' });
        }
    });

    ngAfterViewInit(): void {
        this.loadGoogleMapsApi();
    }

    ngOnDestroy(): void {
        this.clearMarkers();
        if (this.infoWindow) {
            this.infoWindow.close();
        }
        if (this.fitTimer) clearTimeout(this.fitTimer);
        this.setBodyScrollLock(false);
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.expanded()) this.setExpanded(false);
    }

    toggleExpanded(): void {
        this.setExpanded(!this.expanded());
    }

    setExpanded(value: boolean): void {
        if (this.expanded() === value) return;
        this.expanded.set(value);
        this.setBodyScrollLock(value);
        if (!value) {
            this.selectedId.set(null);
            this.infoWindow?.close();
        }
        // The container changes size with the layout; refit once the map has picked that up.
        if (this.fitTimer) clearTimeout(this.fitTimer);
        this.fitTimer = setTimeout(() => this.fitToMarkers(), 150);
    }

    /** Pan to a system from the list and show its details. */
    selectDevice(device: DeviceLocationSummary): void {
        this.selectedId.set(device.deviceId);
        const entry = this.markerByDevice.get(device.deviceId);
        if (!entry || !this.map) return;
        this.map.panTo(entry.marker.getPosition());
        if ((this.map.getZoom() ?? 0) < 15) this.map.setZoom(15);
        this.openInfoWindow(entry.marker, entry.devices);
    }

    openDevice(deviceId: string): void {
        this.setExpanded(false);
        this.router.navigate(['/device', deviceId]);
    }

    private setBodyScrollLock(locked: boolean): void {
        if (typeof document === 'undefined') return;
        document.body.style.overflow = locked ? 'hidden' : '';
    }

    private loadGoogleMapsApi(): void {
        this.mapsLoader
            .load()
            .then(() => this.initializeMap())
            .catch((err: any) => this.error.set(err?.message || 'Failed to load Google Maps'));
    }

    private initializeMap(): void {
        if (!this.mapContainer?.nativeElement) return;

        const isDark = this.layoutService.isDarkTheme();

        this.map = new google.maps.Map(this.mapContainer.nativeElement, {
            center: { lat: 0, lng: 0 },
            zoom: 2,
            styles: isDark ? this.darkMapStyles : this.lightMapStyles,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            // The widget's own expand button replaces the browser full-screen control
            fullscreenControl: false,
            gestureHandling: this.expanded() ? 'greedy' : 'cooperative',
            controlSize: 28,
            colorScheme: isDark ? 'DARK' : 'LIGHT'
        });

        this.infoWindow = new google.maps.InfoWindow();
        this.mapReady.set(true);
        // markerEffect will automatically update markers when mapReady becomes true
    }

    private async loadLocations(namespaceId: string, locationId?: string): Promise<void> {
        this.loading.set(true);
        this.error.set(null);

        try {
            const result = await this.api.getDeviceLocations(namespaceId, locationId);
            this.allLocations.set(result);
            this.loading.set(false);
        } catch (err: any) {
            console.error('Failed to load device locations', err);
            this.error.set(err?.error || 'Failed to load locations');
            this.loading.set(false);
        }
    }

    /** Systems matching the expanded view's search and status filter, sorted by name. */
    filteredLocations = computed(() => {
        const term = this.search().trim().toLowerCase();
        const filter = this.statusFilter();
        return this.allLocations()
            .filter((d) => filter === 'all' || (filter === 'active' ? d.status === 'Active' : d.status !== 'Active'))
            .filter((d) => !term || d.deviceName.toLowerCase().includes(term) || (d.address ?? '').toLowerCase().includes(term))
            .sort((a, b) => a.deviceName.localeCompare(b.deviceName));
    });

    /**
     * Markers on the map: the compact widget caps the count, the expanded view shows every
     * system that matches the list's filters so the two always agree.
     */
    displayedLocations = computed(() => {
        return this.expanded() ? this.filteredLocations() : this.allLocations().slice(0, this.maxMarkers);
    });

    /** Whether there are devices with locations */
    hasLocations = computed(() => this.allLocations().length > 0);

    /** Count of devices with location */
    locationCount = computed(() => this.allLocations().length);

    activeCount = computed(() => this.allLocations().filter((d) => d.status === 'Active').length);

    attentionCount = computed(() => this.locationCount() - this.activeCount());

    /** Count of markers not shown */
    hiddenCount = computed(() => (this.expanded() ? 0 : Math.max(0, this.allLocations().length - this.maxMarkers)));

    statusTone(status: string): 'ok' | 'warn' | 'error' {
        return status === 'Active' ? 'ok' : status === 'Pending' ? 'warn' : 'error';
    }

    sourceLabel(source: DeviceLocationSummary['source'], sourceLocationName?: string | null): string {
        if (source === 'location') return sourceLocationName ? `From ${sourceLocationName}` : 'From its location';
        return source === 'manual' ? 'Manual pin' : source === 'ipEstimate' ? 'Approximate (IP)' : '';
    }

    /** "Active · Manual pin" — status plus where the coordinates came from. */
    statusMeta(d: DeviceLocationSummary): string {
        return [d.status, this.sourceLabel(d.source, d.sourceLocationName)].filter(Boolean).join(' · ');
    }

    private clearMarkers(): void {
        for (const marker of this.markers) {
            marker.setMap(null);
        }
        this.markers = [];
        this.markerByDevice.clear();
    }

    private updateMarkers(locations: DeviceLocationSummary[]): void {
        this.clearMarkers();

        if (locations.length === 0 || !this.map) return;

        // Group devices by approximate location
        const grouped = new Map<string, { lat: number; lng: number; devices: DeviceLocationSummary[] }>();
        for (const loc of locations) {
            const key = `${loc.latitude.toFixed(4)},${loc.longitude.toFixed(4)}`;
            if (!grouped.has(key)) {
                grouped.set(key, { lat: loc.latitude, lng: loc.longitude, devices: [] });
            }
            grouped.get(key)!.devices.push(loc);
        }

        for (const [, group] of grouped) {
            const position = { lat: group.lat, lng: group.lng };

            // Determine marker color based on device status
            const hasActive = group.devices.some((d) => d.status === 'Active');
            const allActive = group.devices.every((d) => d.status === 'Active');

            // IP-estimated positions are approximate — render them softer than exact pins
            const allEstimated = group.devices.every((d) => d.source === 'ipEstimate');

            // Create marker with proper pin
            const marker = new google.maps.Marker({
                position,
                map: this.map,
                title: group.devices.map((d) => d.deviceName).join(', '),
                icon: {
                    path: allEstimated ? google.maps.SymbolPath.CIRCLE : google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                    scale: allEstimated ? 8 : 6,
                    fillColor: allActive ? '#22c55e' : hasActive ? '#f59e0b' : '#ef4444',
                    fillOpacity: allEstimated ? 0.55 : 1,
                    strokeColor: '#ffffff',
                    strokeWeight: allEstimated ? 1 : 2,
                    anchor: new google.maps.Point(0, 0)
                },
                // Add label for multiple devices
                label:
                    group.devices.length > 1
                        ? {
                              text: group.devices.length.toString(),
                              color: '#ffffff',
                              fontSize: '10px',
                              fontWeight: 'bold'
                          }
                        : undefined
            });

            // Click shows the system(s) at this spot; the popup links through to each system
            marker.addListener('click', () => {
                this.ngZone.run(() => {
                    if (this.expanded()) this.selectedId.set(group.devices[0].deviceId);
                    this.openInfoWindow(marker, group.devices);
                });
            });

            for (const d of group.devices) {
                this.markerByDevice.set(d.deviceId, { marker, devices: group.devices });
            }
            this.markers.push(marker);
        }

        this.fitToMarkers();
    }

    /** Fit the viewport to every marker currently on the map. */
    private fitToMarkers(): void {
        if (!this.map || this.markers.length === 0) return;
        if (this.markers.length === 1) {
            this.map.setCenter(this.markers[0].getPosition());
            this.map.setZoom(14);
            return;
        }
        const bounds = new google.maps.LatLngBounds();
        for (const marker of this.markers) bounds.extend(marker.getPosition());
        this.map.fitBounds(bounds, { padding: 50 });
    }

    private openInfoWindow(marker: any, devices: DeviceLocationSummary[]): void {
        if (!this.infoWindow) return;
        this.infoWindow.setContent(this.buildInfoWindowContent(devices));
        this.infoWindow.open({ map: this.map, anchor: marker });
    }

    /**
     * Built from DOM nodes (not an HTML string) so system names and addresses can never inject
     * markup, and so "Open system" can route in-app instead of reloading the page.
     */
    buildInfoWindowContent(devices: DeviceLocationSummary[]): HTMLElement {
        const root = document.createElement('div');
        root.className = 'device-map-popup';

        for (const d of devices) {
            const item = document.createElement('div');
            item.className = 'popup-item';

            const title = document.createElement('div');
            title.className = 'popup-title';
            const dot = document.createElement('span');
            dot.className = `status-dot ${this.statusTone(d.status)}`;
            const name = document.createElement('span');
            name.className = 'popup-name';
            name.textContent = d.deviceName;
            title.append(dot, name);
            item.append(title);

            const metaEl = document.createElement('div');
            metaEl.className = 'popup-meta';
            metaEl.textContent = this.statusMeta(d);
            item.append(metaEl);

            if (d.address) {
                const address = document.createElement('div');
                address.className = 'popup-meta';
                address.textContent = d.address;
                item.append(address);
            }

            const actions = document.createElement('div');
            actions.className = 'popup-actions';
            const open = document.createElement('button');
            open.type = 'button';
            open.className = 'popup-open';
            open.textContent = 'Open system';
            open.addEventListener('click', () => this.ngZone.run(() => this.openDevice(d.deviceId)));
            const directions = document.createElement('a');
            directions.className = 'popup-directions';
            directions.href = `https://www.google.com/maps/dir/?api=1&destination=${d.latitude},${d.longitude}`;
            directions.target = '_blank';
            directions.rel = 'noopener';
            directions.textContent = 'Directions';
            actions.append(open, directions);
            item.append(actions);

            root.append(item);
        }

        return root;
    }

    // Dark mode styles for Google Maps
    private darkMapStyles = [
        { elementType: 'geometry', stylers: [{ color: '#212121' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
        { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
        { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
        { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
        { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
        { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
        { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
        { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
        { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] }
    ];

    // Light mode styles
    private lightMapStyles = [
        { featureType: 'all', stylers: [{ saturation: -20 }] },
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
        { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }
    ];
}
